import DeleteIcon from '@mui/icons-material/Delete';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PostAuthor, TaskFormPayload } from 'api';
import {
  Backdrop,
  BaseTextFieldController,
  DatePicker,
  Form,
  FormGroup,
  FormCancelButton,
  FormSubmitButton,
  MediaUploadZone,
  SelectController,
  SliderController,
  FormAttachments,
  Avatar,
  RichEditorController,
  NewConfirmDialog,
} from 'components';
import { TIME_DATE_FORMAT_WITH_COMMA } from 'constants/index';
import { multiSelectStyles as documentMultiSelectStyles } from 'features/documentCategories/components/CreateDocumentCategoriesForm/styles';
import { useWindowSize } from 'hooks';
import { textFieldStyles, textFieldMobileStyles, richEditorStyles } from 'styles';
import { TaskFormValues, User } from 'types';
import { formattedDate, getFullName } from 'utils';
import { required } from 'validations';

import { AllOption, priorityOptions, statusOptions, TASK_FIELDS_LENGTH } from './constants';
import { useTaskForm } from './hooks';
import { descriptionFieldRules, titleFieldRules } from './rules';
import {
  DeleteButtonW,
  FormContainer,
  FormFooter,
  HeaderAuthorInfo,
  HeaderAuthorName,
  HeaderDateTime,
  HeaderLeft,
  HeaderW,
  LabelTitle,
  LastUpdateW,
} from './styled';
import { avatarSx, descriptionWrapSx, descriptionMobileWrapSx, multiSelectStyles } from './styles';

interface TaskFormProps {
  mode: 'create' | 'edit';
  handleSubmit: (data: TaskFormPayload, postId: string) => void;
  draftId: string;
  defaultValues: TaskFormValues;
  isSubmitting?: boolean;
  updatedBy?: string;
  updatedAt?: string;
  author?: PostAuthor;
  usersList: User[];
  createdAt?: string;
  canDelete?: boolean;
  handleOpenModal?: () => void;
  shouldReset?: boolean;
  setShouldReset?: (value: boolean) => void;
  onClose?: () => void;
}

export function TaskForm({
  draftId,
  handleSubmit,
  mode,
  defaultValues,
  isSubmitting,
  updatedBy,
  updatedAt,
  author,
  usersList,
  createdAt,
  canDelete,
  handleOpenModal,
  shouldReset,
  setShouldReset,
  onClose,
}: TaskFormProps): ReactElement {
  const {
    control,
    isOpen,
    isLoading,
    attachedImages,
    attachedDocs,
    attachedVideos,
    isDirty,
    usersOptions,
    progress,
    handleSubmitConfirmModal,
    handleUploadAll,
    handleDeleteAttachment,
    handleFormSubmit,
    handleCloseConfirmModal,
    handleAssigneeChange,
  } = useTaskForm({
    draftId,
    handleSubmit,
    defaultValues,
    usersList,
    shouldReset,
    setShouldReset,
    onClose,
  });
  const navigate = useNavigate();
  const { isMobile } = useWindowSize();

  const { t } = useTranslation();

  const isEditMode = mode === 'edit';
  const loading = isLoading || isSubmitting;
  const formGroupMarginBottom = isMobile ? 8 : 24;
  const fieldStyles = isMobile ? textFieldMobileStyles : textFieldStyles;

  return (
    <>
      {loading && <Backdrop progress={progress} />}

      <FormContainer className={isEditMode ? 'editMode' : ''}>
        {isEditMode && author && (
          <HeaderW>
            <HeaderLeft>
              <Avatar
                size={isMobile ? 'xs' : 's'}
                userId={author.id}
                filename={author.avatar?.small || ''}
                wrapperSx={avatarSx}
              />
              <HeaderAuthorInfo>
                <HeaderAuthorName>
                  {getFullName({ firstName: author.first_name, lastName: author.last_name })}
                </HeaderAuthorName>
                {createdAt && <HeaderDateTime>{createdAt}</HeaderDateTime>}
              </HeaderAuthorInfo>
            </HeaderLeft>

            {canDelete && handleOpenModal && (
              <DeleteButtonW
                onClick={handleOpenModal}
                startIcon={<DeleteIcon />}
                variant="outlined"
              >
                {t('tasks.details.removeButton')}
              </DeleteButtonW>
            )}
          </HeaderW>
        )}

        <Form onSubmit={handleFormSubmit}>
          <FormGroup marginBottom={formGroupMarginBottom}>
            <LabelTitle>{t('task.create.form.placeholders.title')}</LabelTitle>
            <BaseTextFieldController
              name="title"
              control={control}
              placeholder={t('task.create.form.placeholders.title')}
              sx={fieldStyles}
              maxLength={TASK_FIELDS_LENGTH.title.max}
              rules={titleFieldRules}
              preventSpaceOnStart
            />
          </FormGroup>
          <FormGroup marginBottom={formGroupMarginBottom}>
            <LabelTitle>{t('task.create.form.placeholders.status')}</LabelTitle>
            <SelectController
              name="status"
              control={control}
              placeholder={t('task.create.form.placeholders.status')}
              options={statusOptions}
              sx={multiSelectStyles(isMobile)}
              rules={required}
              type="status"
            />
          </FormGroup>
          <FormGroup marginBottom={formGroupMarginBottom}>
            <LabelTitle>{t('task.create.form.placeholders.priority')}</LabelTitle>
            <SelectController
              name="priority"
              control={control}
              placeholder={t('task.create.form.placeholders.priority')}
              options={priorityOptions}
              rules={required}
              sx={multiSelectStyles(isMobile)}
              type="status"
            />
          </FormGroup>
          <FormGroup marginBottom={formGroupMarginBottom}>
            <LabelTitle>{t('task.create.form.placeholders.progress')}</LabelTitle>
            <SliderController name="progress" control={control} />
          </FormGroup>
          <FormGroup marginBottom={formGroupMarginBottom}>
            <LabelTitle>{t('task.create.form.placeholders.assignee')}</LabelTitle>
            <SelectController
              name="assignee"
              control={control}
              placeholder={t('task.create.form.placeholders.assignee')}
              options={[AllOption, ...usersOptions]}
              multipleSelect
              sx={documentMultiSelectStyles(isMobile)}
              handleChange={handleAssigneeChange}
              type="assignee"
            />
          </FormGroup>

          <FormGroup
            sx={isMobile ? descriptionMobileWrapSx : descriptionWrapSx}
            marginBottom={formGroupMarginBottom}
            row
          >
            <LabelTitle>{t('task.create.form.placeholders.description')}</LabelTitle>
            <RichEditorController
              name="description"
              control={control}
              placeholder={t('task.create.form.placeholders.description')}
              maxLength={TASK_FIELDS_LENGTH.description.max}
              rules={descriptionFieldRules}
              sx={{
                ...richEditorStyles(isMobile),
                ...(isMobile ? { maxWidth: '100%' } : { minWidth: '551px' }),
              }}
            />
          </FormGroup>
          <FormGroup marginBottom={formGroupMarginBottom}>
            <LabelTitle>{t('task.create.form.placeholders.deadline')}</LabelTitle>
            <DatePicker
              name="deadline"
              control={control}
              placeholder={t('task.create.form.placeholders.deadline')}
              sx={fieldStyles}
            />
          </FormGroup>

          <FormAttachments
            attachedImages={attachedImages}
            attachedDocs={attachedDocs}
            attachedVideos={attachedVideos}
            handleDeleteAttachment={handleDeleteAttachment}
            draftId={draftId}
            hasDownloadDoc={isEditMode}
          />
          <MediaUploadZone onUpload={(files) => handleUploadAll(files, draftId)} />

          <FormFooter>
            {updatedBy && updatedAt && (
              <LastUpdateW>
                <span>
                  {t('tasks.details.updateBy', {
                    fullName: updatedBy,
                  })}
                </span>
                <span>{formattedDate(new Date(updatedAt), TIME_DATE_FORMAT_WITH_COMMA)}</span>
              </LastUpdateW>
            )}
            {isEditMode ? (
              <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                <FormCancelButton
                  variant="outlined"
                  onClick={() => (onClose ? onClose() : navigate(-1))}
                >
                  {t('common.cancel')}
                </FormCancelButton>
                <FormSubmitButton type="submit" disabled={isEditMode && !isDirty}>
                  {isEditMode ? t('common.save') : t('common.create')}
                </FormSubmitButton>
              </div>
            ) : null}
            {!isEditMode && (
              <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                <FormCancelButton
                  variant="outlined"
                  onClick={() => (onClose ? onClose() : navigate(-1))}
                >
                  {t('common.cancel')}
                </FormCancelButton>
                <FormSubmitButton onClick={handleFormSubmit}>{t('common.create')}</FormSubmitButton>
              </div>
            )}
          </FormFooter>
        </Form>
      </FormContainer>

      {isOpen && (
        <NewConfirmDialog
          title={t('task.create.modal.title')}
          subTitle={t('task.create.modal.exit')}
          open={isOpen}
          onClose={handleCloseConfirmModal}
          onConfirm={handleSubmitConfirmModal}
          submitLabel={t('task.create.modal.successButton')}
          cancelLabel={t('task.create.modal.cancelButton')}
        />
      )}
    </>
  );
}
